import { commonComponents as cc } from "@/components/common";
import { diaryByIdQueryOptions } from "@/features/diaries/api";
import { redirectIfConsentRequired } from "@/features/consent/utils/redirectIfConsentRequired";
import { PatientSetupPage } from "@/features/diaries/components/PatientSetupPage";
import { diaryKeys } from "@/features/diaries/api/diaryKeys";
import { applyMissingReferenceDate } from "@/features/diaries/api/applyMissingReferenceDate";
import { buildMissingSideItems } from "@/features/diaries/utils/buildMissingSideItems";
import { getReferenceDateContextFromDiary } from "@/features/diaries/utils/getProcedureInformationFromDiary";
import { getPostSetupTarget } from "@/features/diaries/utils/postSetupNavigation";
import { diaryIncludesReferenceDateSetup } from "@/features/diaries/utils/setupNavigation";
import {
    buildMissingReferenceDateAttributeIds,
    patientNeedsHospitalNumberSetup,
} from "@/features/diaries/utils/missingDetailsBuilders";
import { setReferenceDateSetupSkipped } from "@/features/diaries/utils/referenceDateSetupSkip";
import type { SharedAttributeJSON } from "@/features/shared_attributes/types";
import { useDiaryCopy } from "@/features/i18n/diaryCopy";
import { useActionCopy } from "@/features/i18n/actionCopy";
import { useAppLocale } from "@/features/i18n/LocaleProvider";
import { useSetupCopy } from "@/features/i18n/setupCopy";
import { getToastCopy } from "@/features/i18n/toastCopy";
import { hooks as h } from "@/hooks";
import type { StatefulDiaryT } from "@/types/database/diaries";
import { useAuth } from "@/utils/auth";
import { DateTime } from "@patient-watch/forms";
import { Form } from "@patient-watch/ui/form";
import { toast } from "@patient-watch/ui/sonner";
import { useMutation, useQueryClient } from "@tanstack/react-query";
import { createFileRoute, redirect, useNavigate, useRouter } from "@tanstack/react-router";
import dayjs from "dayjs";
import { useForm } from "react-hook-form";

interface ReferenceDateFormValues {
    reference_date: string | null;
}

const REFERENCE_DATE_YEAR_WINDOW = 2;

function collectReferenceDateAttributeIds(diary: StatefulDiaryT): string[] {
    const missing = buildMissingReferenceDateAttributeIds(diary);
    if (missing.length > 0) return missing;

    const ids = new Set<string>();
    const add = (sa: SharedAttributeJSON | null | undefined) => {
        if (sa?.shared_attribute_id) ids.add(sa.shared_attribute_id);
    };
    add(diary.shared_attributes as SharedAttributeJSON | undefined);
    for (const t of diary.trackers_view ?? []) {
        add((t as { shared_attributes?: SharedAttributeJSON | null })?.shared_attributes);
    }
    for (const e of diary.events_view ?? []) {
        add((e as { shared_attributes?: SharedAttributeJSON | null })?.shared_attributes);
    }
    return [...ids];
}

function existingReferenceDate(diary: StatefulDiaryT): string | null {
    const check = (sa: SharedAttributeJSON | null | undefined): string | null => {
        if (!sa?.reference_date) return null;
        return String(sa.reference_date);
    };
    return (
        check(diary.shared_attributes as SharedAttributeJSON | undefined) ??
        (diary.trackers_view ?? [])
            .map((t) => check((t as { shared_attributes?: SharedAttributeJSON | null })?.shared_attributes))
            .find(Boolean) ??
        (diary.events_view ?? [])
            .map((e) => check((e as { shared_attributes?: SharedAttributeJSON | null })?.shared_attributes))
            .find(Boolean) ??
        null
    );
}

const SetupReferenceDatePage = () => {
    const { diaryId } = Route.useParams();
    const diaryCopy = useDiaryCopy();
    const setupCopy = useSetupCopy();
    const actionCopy = useActionCopy();
    const { locale } = useAppLocale();
    const auth = useAuth();
    const navigate = useNavigate({ from: Route.fullPath });
    const router = useRouter();
    const queryClient = useQueryClient();
    const { sharedAttributeIds, diary } = Route.useLoaderData() as {
        sharedAttributeIds: string[];
        diary: StatefulDiaryT;
    };
    const referenceDateContext = getReferenceDateContextFromDiary(diary, locale);
    const copy = setupCopy.getReferenceDateSetupCopy(referenceDateContext.heading, referenceDateContext.kind);
    const minDate = dayjs().subtract(REFERENCE_DATE_YEAR_WINDOW, "year").startOf("day").toDate();
    const maxDate = dayjs().add(REFERENCE_DATE_YEAR_WINDOW, "year").endOf("day").toDate();
    const form = useForm<ReferenceDateFormValues>({
        defaultValues: { reference_date: existingReferenceDate(diary) },
    });

    const goToNextStep = async () => {
        // Detail drives diary routes while byPatient drives profile/diary-list progress.
        // Invalidate the shared prefix so every progress surface sees the same state.
        await queryClient.invalidateQueries({ queryKey: diaryKeys.all });
        const latestDiary = await queryClient.fetchQuery(diaryByIdQueryOptions(diaryId));
        if (latestDiary && buildMissingSideItems(latestDiary).length > 0) {
            void navigate({
                to: "/diaries/$diaryId/setup/side",
                params: { diaryId },
                search: {},
            });
            return;
        }
        const target = await getPostSetupTarget(diaryId, auth.id);
        void navigate(target);
    };

    const mutation = useMutation({
        mutationFn: async (date: string | null) => {
            if (!date) {
                throw new Error(copy.chooseDateError);
            }
            const selected = dayjs(date);
            if (selected.isBefore(dayjs(minDate), "day")) {
                throw new Error(copy.minMessage);
            }
            if (selected.isAfter(dayjs(maxDate), "day")) {
                throw new Error(copy.maxMessage);
            }
            if (sharedAttributeIds.length === 0) {
                throw new Error(setupCopy.missingReferenceDateDataError);
            }
            await applyMissingReferenceDate(sharedAttributeIds, date);
        },
        onSuccess: async () => {
            await setReferenceDateSetupSkipped(diaryId, false);
            toast.success(copy.savedToast);
            await goToNextStep();
        },
        onError: (err) => {
            toast.error(err instanceof Error ? err.message : getToastCopy().couldNotSaveProcedureDate);
        },
    });

    const handleSkip = async () => {
        try {
            await setReferenceDateSetupSkipped(diaryId, true);
            toast.success(getToastCopy().savedForNow);
            await goToNextStep();
        } catch (error) {
            toast.error(error instanceof Error ? error.message : getToastCopy().couldNotSaveProcedureDate);
        }
    };

    h.useNav([
        { route: { to: "/diaries" }, label: diaryCopy.diaries },
        {
            route: { to: "/diaries/$diaryId/home/to-do", params: { diaryId } },
            label: diaryCopy.diary,
        },
    ]);

    return (
        <PatientSetupPage title={copy.title} description={copy.description}>
            <Form {...form}>
                <form
                    className="flex flex-col flex-1 gap-y-6"
                    onSubmit={form.handleSubmit((values) => mutation.mutate(values.reference_date))}
                >
                    <DateTime
                        name="reference_date"
                        label={copy.fieldLabel}
                        datePlaceholder={setupCopy.pickDate}
                        clearLabel={setupCopy.clearDateAndTime}
                        minDate={minDate}
                        maxDate={maxDate}
                        minMessage={copy.minMessage}
                        maxMessage={copy.maxMessage}
                    />
                    <div className="!mt-auto">
                        <cc.FooterButtons
                            disabled1={mutation.isPending}
                            disabled2={mutation.isPending}
                            disabled3={mutation.isPending}
                            isLoading1={mutation.isPending}
                            type1="submit"
                            type2="button"
                            words1={copy.saveButton}
                            words2={actionCopy.back}
                            words3={setupCopy.skipForNow}
                            func2={() => router.history.back()}
                            func3={() => {
                                void handleSkip();
                            }}
                            primaryAction="save"
                            secondaryAction="back"
                            tertiaryAction="skip"
                        />
                    </div>
                </form>
            </Form>
        </PatientSetupPage>
    );
};

export const Route = createFileRoute("/_app/diaries/$diaryId/setup/reference-date")({
    component: SetupReferenceDatePage,
    loader: async ({ context, params }) => {
        const diary = (await context?.queryClient.ensureQueryData(
            diaryByIdQueryOptions(params.diaryId)
        )) as StatefulDiaryT | null;
        if (!diary) {
            throw redirect({ to: "/diaries" });
        }
        const isPatientViewer = (context.auth?.roles?.isPatientOnly ?? false) || context.auth?.id === diary.patient_id;
        if (!isPatientViewer) {
            throw redirect({
                to: "/diaries/$diaryId/home/to-do",
                params: { diaryId: params.diaryId },
            });
        }

        redirectIfConsentRequired(diary, context.auth?.id, isPatientViewer);

        if (patientNeedsHospitalNumberSetup(diary)) {
            throw redirect({
                to: "/diaries/$diaryId/setup/hospital-number",
                params: { diaryId: params.diaryId },
            });
        }

        // The template set the date to the start date (or the patient already gave it): nothing to ask.
        if (!diaryIncludesReferenceDateSetup(diary)) {
            throw redirect(await getPostSetupTarget(params.diaryId, context.auth?.id ?? ""));
        }

        return {
            diary,
            sharedAttributeIds: collectReferenceDateAttributeIds(diary),
        };
    },
});
